/**
 * The form that finds a booking the visitor already has (docs-company ADR-110
 * Amendment 10, `find_booking`): its reference, and ONE box for the email or
 * phone it was made with. Drawn exactly like the details form (DetailsForm.tsx)
 * — a card in the transcript, labelled boxes, each refusal under its own box
 * with the caret put there — and, like the booking's details, it posts nowhere
 * of its own: the values ride the next /chat/message turn as `lookup`, and the
 * visitor's bubble carries the reference alone (actions.ts `lookupLine`).
 *
 * Why a form and not a question: the email or phone is a contact detail, and
 * contact details never travel as chat text (ADR-106, ADR-082) — typed into
 * the conversation they would reach the model and the transcript.
 */
import { useEffect, useId, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { LOOKUP_LIMITS, validateLookup, type BookingLookup } from './actions';
import type { WidgetStrings } from './strings';
import type { Styles } from './theme';

export interface FindBookingFormProps {
  ui: WidgetStrings;
  styles: Styles;
  brandColor: string;
  /** Sends the lookup as the next turn; false when nothing was sent, so the form stays usable. */
  onSubmit: (lookup: BookingLookup) => boolean;
  /** Closes the form and leaves the conversation as it is: typing still works. */
  onCancel: () => void;
}

type Field = 'booking_reference' | 'booking_contact';

export function FindBookingForm({ ui, styles, brandColor, onSubmit, onCancel }: FindBookingFormProps) {
  const [reference, setReference] = useState('');
  const [contact, setContact] = useState('');
  const [error, setError] = useState<Field | null>(null);
  const [busy, setBusy] = useState(false);
  const inputs = useRef<Record<Field, HTMLInputElement | null>>({ booking_reference: null, booking_contact: null });
  const id = useId();

  useEffect(() => {
    inputs.current.booking_reference?.focus();
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const v = validateLookup(reference, contact);
    if (!v.ok) {
      setError(v.field);
      inputs.current[v.field]?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    if (!onSubmit(v.value)) setBusy(false);
  };

  const labelStyle: CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', margin: '8px 0 4px' };
  const card: CSSProperties = { ...styles.assistantBubble, alignSelf: 'stretch', maxWidth: '100%', padding: '12px 14px' };
  const primary: CSSProperties = {
    ...styles.chip,
    background: brandColor,
    borderColor: brandColor,
    color: styles.ink,
    opacity: busy ? 0.7 : 1,
    cursor: busy ? 'default' : 'pointer',
  };
  const titleId = `${id}-title`;
  const errorId = (field: Field) => `${id}-${field}-error`;
  const message: Record<Field, string> = {
    booking_reference: ui.findBookingReferenceInvalid,
    booking_contact: ui.findBookingContactInvalid,
  };

  const box = (field: Field, label: string, value: string, set: (v: string) => void, extra: Record<string, string | number>) => (
    <div data-pwe={`find-booking-field-${field}`}>
      <label htmlFor={`${id}-${field}`} style={labelStyle}>
        {label}
        <span aria-hidden="true"> *</span>
      </label>
      <input
        id={`${id}-${field}`}
        ref={(el) => {
          inputs.current[field] = el;
        }}
        name={field}
        value={value}
        onChange={(e) => {
          set(e.target.value);
          if (error === field) setError(null);
        }}
        required
        aria-invalid={error === field || undefined}
        aria-describedby={error === field ? errorId(field) : undefined}
        style={styles.formField}
        {...extra}
      />
      {error === field && (
        <p id={errorId(field)} role="alert" style={styles.formError}>
          {message[field]}
        </p>
      )}
    </div>
  );

  return (
    <form onSubmit={submit} style={card} aria-labelledby={titleId} aria-busy={busy} noValidate data-pwe="find-booking-form">
      <div id={titleId} style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>
        {ui.findBookingTitle}
      </div>
      {box('booking_reference', ui.findBookingReference, reference, setReference, {
        type: 'text',
        autoComplete: 'off',
        autoCapitalize: 'characters',
        spellCheck: 'false',
        maxLength: LOOKUP_LIMITS.reference,
      })}
      {box('booking_contact', ui.findBookingContact, contact, setContact, {
        type: 'text',
        autoComplete: 'email',
        maxLength: LOOKUP_LIMITS.email,
      })}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
        <button type="button" onClick={onCancel} style={styles.chip} disabled={busy}>
          {ui.leadCancel}
        </button>
        <button type="submit" style={primary} disabled={busy}>
          {busy ? ui.leadSending : ui.findBookingSubmit}
        </button>
      </div>
    </form>
  );
}
